import { useEffect, useRef, useState } from 'react';
import { Check, Delete } from 'lucide-react';
import { useAppTheme } from '../hooks/useAppTheme';
import { useT } from '../hooks/useT';

interface InputKeypadTurnTotalProps {
  onSubmitTotal: (total: number) => void;
}

const QUICK_PRESETS = [180, 140, 100, 85, 60, 45, 41, 26, 0];
// Totalen die met 3 darts niet te gooien zijn.
const IMPOSSIBLE_SCORES = [179, 178, 176, 175, 173, 172, 169, 166, 163];
const AUTO_SUBMIT_MS = 3000;

const triggerHaptic = () => {
  try {
    navigator.vibrate?.(10);
  } catch {
    // trillen is een extraatje
  }
};

export function InputKeypadTurnTotal({ onSubmitTotal }: InputKeypadTurnTotalProps) {
  const { t, tf } = useT();
  const { theme, isDark } = useAppTheme();
  const [valStr, setValStr] = useState('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const autoSubmitTimer = useRef<number | undefined>(undefined);
  // De automatische bevestiging loopt na een paar seconden; gebruik dan de nieuwste handler (en dus de nieuwste stand).
  const onSubmitRef = useRef(onSubmitTotal);
  onSubmitRef.current = onSubmitTotal;

  const clearAutoSubmit = () => window.clearTimeout(autoSubmitTimer.current);

  const validate = (num: number): string | null => {
    if (isNaN(num) || num < 0 || num > 180) return t('invalidScore');
    if (IMPOSSIBLE_SCORES.includes(num)) return tf('impossibleScore', { n: num });
    return null;
  };

  const submit = (num: number) => {
    clearAutoSubmit();
    const error = validate(num);
    if (error) {
      setErrorMsg(error);
      return;
    }
    onSubmitRef.current(num);
    setValStr('');
    setErrorMsg(null);
  };

  const scheduleAutoSubmit = (value: string) => {
    clearAutoSubmit();
    if (!value) return;
    const num = parseInt(value, 10);
    if (validate(num)) return;
    autoSubmitTimer.current = window.setTimeout(() => submit(num), AUTO_SUBMIT_MS);
  };

  const handleDigit = (digit: string) => {
    triggerHaptic();
    setErrorMsg(null);
    if (valStr.length >= 3) return;
    const next = valStr + digit;
    const num = parseInt(next, 10);
    if (num > 180) {
      clearAutoSubmit();
      setErrorMsg(t('maxScore'));
      return;
    }
    setValStr(next);
    if (next.length === 3) submit(num);
    else scheduleAutoSubmit(next);
  };

  const handleDelete = () => {
    triggerHaptic();
    setErrorMsg(null);
    const next = valStr.slice(0, -1);
    setValStr(next);
    scheduleAutoSubmit(next);
  };

  const handleClear = () => {
    triggerHaptic();
    clearAutoSubmit();
    setErrorMsg(null);
    setValStr('');
  };

  const handleConfirm = () => {
    triggerHaptic();
    if (valStr) submit(parseInt(valStr, 10));
  };

  useEffect(() => () => window.clearTimeout(autoSubmitTimer.current), []);

  const keyButton = `min-h-14 w-full rounded-2xl font-mono text-2xl font-bold transition-all duration-150 active:scale-95 ${
    isDark ? theme.keypadNumberBg : 'bg-white shadow-sm'
  } ${theme.keypadNumberHover} ${theme.keypadNumberActiveBg} ${theme.keypadNumberText}`;
  const primary = `${theme.keypadPrimaryBg} ${theme.keypadPrimaryText}`;

  return (
    <div className="mx-auto w-full max-w-xl space-y-2.5 rounded-3xl bg-black/5 p-3 backdrop-blur-xl dark:bg-black/40 sm:p-4">
      <div className="flex min-h-16 items-center justify-between gap-2 rounded-2xl bg-white px-3 py-2 shadow-sm dark:bg-white/5">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-slate-600 uppercase dark:text-slate-400">{t('thrownScore')}</span>
            {valStr && <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${theme.badgeBg} ${theme.badgeText}`}>{t('autoConfirm')}</span>}
          </div>
          <output aria-live="polite" className={`block h-9 font-mono text-3xl font-bold ${theme.textScore}`}>
            {valStr || <span className="text-slate-300 dark:text-slate-600">0</span>}
          </output>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {valStr && (
            <button
              type="button"
              onClick={handleClear}
              className="min-h-11 rounded-2xl bg-black/5 px-3 text-sm font-semibold whitespace-nowrap transition-all duration-150 active:scale-95 dark:bg-white/10"
            >
              {t('clearInput')}
            </button>
          )}
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!valStr}
            className={`flex min-h-11 items-center gap-1.5 rounded-2xl px-4 text-sm font-bold whitespace-nowrap transition-all duration-150 active:scale-95 disabled:opacity-30 ${primary}`}
          >
            <Check size={16} aria-hidden />
            {t('enterScore')}
          </button>
        </div>
      </div>

      {errorMsg && (
        <p role="alert" className="rounded-2xl bg-red-500/15 px-3 py-2 text-center text-sm font-semibold text-red-800 dark:text-red-300">
          {errorMsg}
        </p>
      )}

      <div>
        <p className="mb-1 text-xs font-semibold text-slate-600 uppercase dark:text-slate-400">{t('presetsLabel')}</p>
        <div className="grid grid-cols-5 gap-1.5 font-mono sm:grid-cols-9">
          {QUICK_PRESETS.map(score => (
            <button
              key={score}
              type="button"
              onClick={() => {
                triggerHaptic();
                submit(score);
              }}
              className={`min-h-11 rounded-2xl text-sm font-bold transition-all duration-150 active:scale-95 ${
                score === 180
                  ? 'bg-amber-500 text-black'
                  : score === 140 || score === 100
                  ? primary
                  : isDark
                  ? `${theme.keypadPresetBg} ${theme.keypadPresetText}`
                  : 'bg-white text-slate-800 shadow-sm hover:bg-slate-50'
              }`}
            >
              {score}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-1.5">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => (
          <button key={num} type="button" onClick={() => handleDigit(String(num))} className={keyButton}>
            {num}
          </button>
        ))}
        <button
          type="button"
          onClick={handleDelete}
          aria-label={t('deleteDigit')}
          className="grid min-h-14 w-full place-items-center rounded-2xl bg-red-500/15 text-red-800 transition-all duration-150 active:scale-95 dark:bg-red-950/50 dark:text-red-300"
        >
          <Delete size={22} aria-hidden />
        </button>
        <button type="button" onClick={() => handleDigit('0')} className={keyButton}>
          0
        </button>
        <button
          type="button"
          onClick={handleConfirm}
          disabled={!valStr}
          className={`min-h-14 w-full rounded-2xl text-base font-bold transition-all duration-150 active:scale-95 disabled:opacity-30 ${primary}`}
        >
          {t('okBtn')}
        </button>
      </div>
    </div>
  );
}
